import { TravelCurrencyCode, TravelOfferRefundability, TravelOfferReservationRefundability } from '@graphql/generated';
import { format, intervalToDuration } from 'date-fns';
import { OfferInfo } from './OfferSummary';
import { OfferPolicy } from '@apps/stays/EventAccommodations/routes/HotelPDPPage/HotelPDPPage.types';
import { translatedPrice } from '@shared/utils/accommodationPrice';
import { Discount, applyDiscount } from '@shared/components/DiscountsProvider';

type CostProperty = Readonly<{
  minorUnits: number;
  display: string;
}>;

/**
 * Converts an amount in minor units to a `CostProperty` object with a formatted display string.
 *
 * @param amount - An object containing:
 *   - valueInMinorUnits: The amount in minor currency units.
 *   - currency: The currency code.
 *   - digits: (Optional) Number of decimal digits for the currency. Defaults to 2 if not provided.
 * @param locale - The locale to use for formatting the currency.
 * @param showDecimals - Whether to show decimals in the formatted display string.
 * @returns A `CostProperty` object with the minor units and a formatted display string.
 */
export const minorUnitsToCostProperty = (
  amount: Readonly<{ valueInMinorUnits: number; currency: TravelCurrencyCode; digits?: number }>,
  locale: Navigator['language'] = 'en-US',
  display: 'rounded' | 'decimal' = 'rounded',
): CostProperty => {
  const digits = amount.digits ?? 2;
  return {
    minorUnits: amount.valueInMinorUnits,
    display: translatedPrice(amount.valueInMinorUnits, digits, amount.currency, locale, display === 'decimal'),
  };
};

const calcNumNights = (start: Date, end: Date) => intervalToDuration({ start, end }).days;
// const numUnits = data.travelOfferFromOfferToken.components.flatMap(c => c.occupancies.length).reduce((tot, numUnits) => tot + numUnits, 0);
const calcTaxesAndFees = (totalCost: number, baseCost: number, currency: TravelCurrencyCode, digits: number, locale: Navigator['language']) => {
  return minorUnitsToCostProperty(
    {
      valueInMinorUnits: totalCost - baseCost,
      // assumed that the total and base have the same currency
      currency: currency,
      digits,
    },
    locale,
    'decimal',
  );
};

const calculatePropertyPriceBreakDown = (
  totalAmount: { valueInMinorUnits: number; currency: TravelCurrencyCode; digits: number },
  locale: Navigator['language'],
  onsiteCharges?: ReadonlyArray<OnsiteCharge>,
) => {
  const amountDueAtProperty = onsiteCharges?.reduce((total, charge) => {
    return total + charge.unitPrice.valueInMinorUnits;
  }, 0);

  const totalPayable = amountDueAtProperty ? totalAmount.valueInMinorUnits + amountDueAtProperty : totalAmount.valueInMinorUnits;

  return {
    totalPayable: minorUnitsToCostProperty({ valueInMinorUnits: totalPayable, currency: totalAmount.currency, digits: totalAmount.digits }, locale, 'decimal'),
    amountDueNow: minorUnitsToCostProperty(totalAmount, locale, 'decimal'),
    amountDueAtProperty: amountDueAtProperty ? minorUnitsToCostProperty({ valueInMinorUnits: amountDueAtProperty, currency: totalAmount.currency }, locale, 'decimal') : undefined,
  };
};

const calcAveragePricePerNight = (baseCost: number, numNights: number, currency: TravelCurrencyCode, locale: Navigator['language']) => {
  return minorUnitsToCostProperty(
    {
      valueInMinorUnits: Math.floor(baseCost / numNights),
      currency,
    },
    locale,
    'decimal',
  );
};

type OnsiteCharge = Readonly<{
  title: string;
  unitPrice: { valueInMinorUnits: number; currency: TravelCurrencyCode; digits?: number };
  units?: Maybe<string[]>;
}>;

const filterAndCombineOnsiteCharges = (onSiteCharges: readonly OnsiteCharge[]): readonly OnsiteCharge[] => {
  const groupedCharges = onSiteCharges.reduce(
    (acc, charge) => {
      if (!acc[charge.title]) acc[charge.title] = [];
      acc[charge.title].push(charge);
      return acc;
    },
    {} as Record<string, OnsiteCharge[]>,
  );

  return Object.keys(groupedCharges).map(title => {
    const charges = groupedCharges[title];
    if (charges.length === 1) return charges[0];

    const allUnits: string[] = [];
    charges.forEach(charge => {
      if (Array.isArray(charge.units)) {
        allUnits.push(...charge.units);
      }
    });

    const totalValueInMinorUnits = charges.reduce((sum, charge) => sum + charge.unitPrice.valueInMinorUnits, 0);
    const digits = charges[0].unitPrice.digits || 2;
    const divisor = Math.pow(10, digits);

    return {
      ...charges[0],
      unitPrice: {
        ...charges[0].unitPrice,
        floatingPointDecimalString: (totalValueInMinorUnits / divisor).toFixed(digits),
        valueInMinorUnits: totalValueInMinorUnits,
      },
      units: Array.from(new Set(allUnits)),
    };
  });
};

const calcOnsiteCharges = (onsiteCharges: ReadonlyArray<OnsiteCharge>, locale: Navigator['language']) => {
  const combinedOnSiteCharges = filterAndCombineOnsiteCharges(onsiteCharges);

  return combinedOnSiteCharges.map(charge => {
    const amount = minorUnitsToCostProperty(
      {
        valueInMinorUnits: charge.unitPrice.valueInMinorUnits,
        currency: charge.unitPrice.currency,
        digits: charge.unitPrice.digits,
      },
      locale,
      'decimal',
    );

    return {
      amount,
      display: charge.title,
    };
  });
};

type Component = {
  unitId: string;
  unitName: string;
  numberAdults: number;
  numberChildren: number;
  childrenAges?: number[] | null;
  refundability: TravelOfferRefundability | TravelOfferReservationRefundability;
};

const formatAges = (ages: number[]): string => {
  if (!ages.length) return '';
  if (ages.length === 1) return `Age ${ages[0]}`;
  return `Ages ${ages.slice(0, -1).join(', ')} and ${ages[ages.length - 1]}`;
};

const unitsForOfferInfo = (components: Component[]) => {
  return components.map(c => {
    const childrenAgesDisplay = c.childrenAges && c.childrenAges.length ? `(${formatAges([...c.childrenAges].sort((a, b) => a - b))})` : '';
    const occupants = [
      c.numberAdults && `${c.numberAdults} ${c.numberAdults > 1 ? 'Adults' : 'Adult'}`,
      c.numberChildren && `${c.numberChildren} ${c.numberChildren > 1 ? 'Children' : 'Child'} ${childrenAgesDisplay}`,
    ]
      .filter(Boolean)
      .join(', ');
    return {
      unitId: c.unitId,
      unitName: c.unitName,
      occupants: `(${occupants})`,
      numChildren: c.numberChildren || 0,
      numAdults: c.numberAdults || 0,
    };
  });
};

type PriceInfo = Readonly<{
  currency: TravelCurrencyCode;
  valueInMinorUnits: number;
  digits: number;
}>;

type CalOfferInfoParams = Readonly<{
  property: Readonly<{
    description: string | null;
    name: string;
    address?: Readonly<{ line1?: Maybe<string>; line2?: Maybe<string>; city?: Maybe<string>; state?: Maybe<string>; country?: Maybe<string>; postalCode?: Maybe<string> }>;
    photo?: Readonly<{ url: string }>;
  }>;
  components: Component[];
  policies?: Array<OfferPolicy>;
  totalAmount: PriceInfo;
  baseAmount: PriceInfo;
  checkInDay: Date;
  checkOutDay: Date;
  locale: Navigator['language'];
  onsiteCharges?: ReadonlyArray<OnsiteCharge>;
  estimatedCancellationFee?: PriceInfo;
  providerName?: string;
  discountAmount?: Maybe<PriceInfo>;
  discount?: Discount;
}>;

const applyDiscountToAmount = (amount: PriceInfo, discount: Discount | undefined): PriceInfo => {
  return discount
    ? {
        ...amount,
        valueInMinorUnits: applyDiscount(amount.valueInMinorUnits, discount),
      }
    : amount;
};

export const calcOfferInfo = ({
  components,
  property,
  policies,
  totalAmount,
  baseAmount,
  checkInDay,
  checkOutDay,
  onsiteCharges: _onSiteCharges = [],
  estimatedCancellationFee,
  providerName,
  discountAmount,
  discount,
  locale,
}: CalOfferInfoParams): OfferInfo => {
  const totalAfterDiscount = applyDiscountToAmount(totalAmount, discount);
  const baseAfterDiscount = applyDiscountToAmount(baseAmount, discount);
  const beforeDiscountTotalMinorUnits = totalAmount.valueInMinorUnits;
  const propertyPriceBreakDown = calculatePropertyPriceBreakDown(totalAfterDiscount, locale, _onSiteCharges);
  const savingsShown = discountAmount
    ? minorUnitsToCostProperty(discountAmount, locale)
    : discount
      ? minorUnitsToCostProperty(
          {
            valueInMinorUnits: beforeDiscountTotalMinorUnits - totalAfterDiscount.valueInMinorUnits,
            currency: baseAmount.currency,
            digits: baseAmount.digits,
          },
          locale,
        )
      : undefined;
  const baseCost = minorUnitsToCostProperty(baseAfterDiscount, locale, 'decimal');
  const numNights = calcNumNights(checkInDay, checkOutDay);
  if (numNights === undefined) {
    throw new Error('Invalid check in and check out dates');
  }
  const taxesAndFees = calcTaxesAndFees(totalAfterDiscount.valueInMinorUnits, baseAfterDiscount.valueInMinorUnits, totalAmount.currency, totalAmount.digits, locale);

  // hack, UI kind of assumes that there is only 1 room so refundability for the reservation as a whole is inaccurate
  const refundable = components.every(c => c.refundability === TravelOfferRefundability.FullyRefundable);
  const averagePricePerNight = calcAveragePricePerNight(baseAfterDiscount.valueInMinorUnits, numNights, totalAmount.currency, locale);
  const onsiteCharges = calcOnsiteCharges(_onSiteCharges, locale);

  return {
    propertyName: property.name,
    bedTypeDescription: property.description,
    propertyPhoto: property.photo ? { src: property.photo.url, alt: undefined } : undefined,
    propertyAddress: property.address,
    units: unitsForOfferInfo(components),
    totalCost: propertyPriceBreakDown.totalPayable.display,
    baseCost: baseCost.display,
    taxesAndFees: taxesAndFees.display,
    totalSavings: savingsShown?.display,
    discount: discount,
    refundable,
    policies: policies,
    averagePerNight: averagePricePerNight && `Average per night (${averagePricePerNight.display})`,
    checkIn: format(checkInDay, 'eee, MMM dd'),
    checkOut: format(checkOutDay, 'eee, MMM dd'),
    numNights,
    onsiteCharges,
    providerName,
    amountDueAtProperty: propertyPriceBreakDown.amountDueAtProperty?.display,
    amountDueNow: propertyPriceBreakDown.amountDueNow.display,
    // no discount on cancelation fees
    estimatedCancellationFee: estimatedCancellationFee ? minorUnitsToCostProperty(estimatedCancellationFee, locale, 'decimal').display : '',
    estimatedCancellationFeeInPercent: estimatedCancellationFee ? (estimatedCancellationFee.valueInMinorUnits / totalAmount.valueInMinorUnits) * 100 : undefined,
  };
};
